import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { API_BASE_URL, buildPublicShareUrl } from '../config';
import useMenuApproval from '../hooks/useMenuApproval';
import Swal from 'sweetalert2';
import { copyToClipboard } from '../utils/clipboard';

// Alakart Components
import PremiumLockComponent from '../components/PremiumLockComponent';
import AlakartBoard from '../components/AlakartBoard';
import RecipeCard from '../components/RecipeCard';
import RecipeModal from '../components/RecipeModal';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import AlakartRecipeGrid from '../components/AlakartRecipeGrid';
import AlakartConfig from '../components/AlakartConfig';
import AlakartFinalSummary from '../components/AlakartFinalSummary';
import AlakartOtonomGrid from '../components/AlakartOtonomGrid';
import AlakartPhaseZeroSurvey from '../components/AlakartPhaseZeroSurvey';
import AlakartRatingSummary from '../components/AlakartRatingSummary';
import HealthPlusActivationModal from '../components/HealthPlusActivationModal';
import PremiumPurchaseModal from '../components/PremiumPurchaseModal';
import HealthPlusMenuCard from '../components/HealthPlusMenuCard';
import HealthMedicalNotice from '../components/HealthMedicalNotice';
import { promptAndSetShoppingReminder } from '../utils/shoppingReminder';

// Custom Mobile Styles based on user prototype
const mobileStyles = `
  .ios-shadow { box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
  .fab-shadow { box-shadow: 0 8px 24px rgba(239, 68, 68, 0.3); }
  .glass-panel { background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(8px); }
`;

const resolvePlanDays = (plan) => plan?.effectiveDays || plan?.generatedDays || {};

const resolveRecipeSlotCategory = (recipe = {}) => {
    const tokens = [
        recipe?.canonical?.serveStyle,
        recipe?.canonical?.category,
        recipe?.serveStyle,
        recipe?.category,
        recipe?.mealType,
        recipe?.dishType,
        recipe?.title,
        recipe?.name
    ]
        .map((value) => String(value || '').toLocaleLowerCase('tr-TR'))
        .join(' ');

    if (/(çorba|corba|soup)/.test(tokens)) return 'SOUP';
    if (/(salata|salad|meze|piyaz)/.test(tokens)) return 'SALAD';
    if (/(tatlı|tatli|dessert|muhallebi|helva|cake)/.test(tokens)) return 'DESSERT';
    if (/(balık|balik|seafood|fish)/.test(tokens)) return 'SEAFOOD';
    if (/(tavuk|chicken)/.test(tokens)) return 'CHICKEN';
    if (/(et|köfte|kofte|meat|kebap)/.test(tokens)) return 'MEAT';
    if (/(bakliyat|mercimek|nohut|fasulye|legume)/.test(tokens)) return 'LEGUME';
    if (/(pilav|makarna|pasta|rice)/.test(tokens)) return 'PASTA';
    if (/(hamur|poğaça|pogaca|börek|borek|simit|bazlama|pastry)/.test(tokens)) return 'PASTRY';
    return 'MAIN';
};

const getRecipeMetricValue = (recipe = {}, metricKey = null) => {
    if (!metricKey) return Number.MAX_SAFE_INTEGER;
    const summary = recipe?.policyContributionSummary || {};
    const value = Number(summary?.[metricKey] || 0);
    return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

const buildAlakartGenerationErrorHtml = (payload = {}) => {
    const details = Array.isArray(payload?.details) ? payload.details : [];
    if (details.length === 0) {
        return `<p style="margin:0;">${payload?.error || 'Şimdilik planı yeniden üretemedik.'}</p>`;
    }

    const items = details.map((issue) => {
        const currentValue = Number(issue?.currentValue || 0);
        const threshold = Number(issue?.threshold || 0);
        const unit = String(issue?.unit || '').trim();
        const label = String(issue?.label || issue?.message || issue?.type || 'Kural').trim();
        return `
            <li style="margin:0 0 10px 0;">
                <strong>${label}</strong><br/>
                <span>Şu an: ${Number.isFinite(currentValue) ? currentValue : '-'} ${unit}</span><br/>
                <span>Eşik: ${Number.isFinite(threshold) ? threshold : '-'} ${unit}</span>
            </li>
        `;
    }).join('');

    return `
        <div style="text-align:left;">
            <p style="margin:0 0 12px 0;">${payload?.error || 'Sistem planı contract eşiğini geçemedi.'}</p>
            <ul style="margin:0;padding-left:18px;">
                ${items}
            </ul>
        </div>
    `;
};

// Helper to keep draft and otonomPlan in sync
const syncDraftWithPlan = (plan) => {
    const planDays = resolvePlanDays(plan);
    if (!plan || !planDays || Object.keys(planDays).length === 0) return {};
    const newDraft = {};
    Object.entries(planDays).forEach(([date, dayPlan]) => {
        if (date === '_meta') return;
        newDraft[date] = {};
        Object.entries(dayPlan).forEach(([slot, data]) => {
            if (data && (data.id || data.recipeId)) {
                newDraft[date][slot] = data.id || data.recipeId;
            }
        });
    });
    return newDraft;
};

const getAuthHeaders = (extra = {}) => {
    const token = localStorage.getItem('authToken');
    if (token) return { Authorization: `Bearer ${token}`, ...extra };
    return { ...extra };
};

const normalizeAlakartConfigFromPlan = (plan, fallbackPortion = 4) => {
    const targetCategories = Array.isArray(plan?.contextSnapshot?.targetCategories)
        ? plan.contextSnapshot.targetCategories.filter(Boolean)
        : [];
    if (targetCategories.length === 0) return null;

    return {
        portion: Number(plan?.contextSnapshot?.portion || fallbackPortion || 4),
        meals: targetCategories
    };
};

const AlakartMenu = ({ isDarkMode, toggleDarkMode }) => {
    const location = useLocation(); // Add this hook (need to import it if not present, checking imports)
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [recipesLoading, setRecipesLoading] = useState(false);
    const [daySummaryRecipes, setDaySummaryRecipes] = useState([]);
    const [hasPremium, setHasPremium] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [portion, setPortion] = useState(4);
    const [skillLevel, setSkillLevel] = useState('Acemi');
    const [showHealthPlusModal, setShowHealthPlusModal] = useState(false);
    const [showHealthPlusPaymentModal, setShowHealthPlusPaymentModal] = useState(false);
    const [healthPlusActive, setHealthPlusActive] = useState(false);
    const [healthPlusEntitled, setHealthPlusEntitled] = useState(false);
    const [healthPlusBusy, setHealthPlusBusy] = useState(false);

    const fetchHealthPlusState = async () => {
        try {
            const [profileRes, stateRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/profile`, {
                    credentials: 'include',
                    headers: getAuthHeaders()
                }),
                fetch(`${API_BASE_URL}/api/health/activation-state`, {
                    credentials: 'include',
                    headers: getAuthHeaders()
                })
            ]);
            const profileData = await profileRes.json().catch(() => ({}));
            const stateData = await stateRes.json().catch(() => ({}));
            const entitled = !!profileData?.entitlements?.modules?.global?.granted
                && !profileData?.entitlements?.lockedByTime;
            setHealthPlusEntitled(entitled);
            setHealthPlusActive(entitled && !!stateData?.enabled);
        } catch (error) {
            console.warn('health+ state fetch warning', error);
            setHealthPlusEntitled(false);
            setHealthPlusActive(false);
        }
    };

    useEffect(() => {
        fetchHealthPlusState();
    }, []);

    const setHealthPlusActivationState = async (enabled) => {
        try {
            setHealthPlusBusy(true);
            const res = await fetch(`${API_BASE_URL}/api/health/activation-state`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ enabled: !!enabled }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'health_activation_state_update_failed');
            setHealthPlusActive(!!data.enabled);
        } catch (error) {
            console.error('health+ toggle failed', error);
            Swal.fire('Hata', 'Sağlık+ durumu güncellenemedi.', 'error');
        } finally {
            setHealthPlusBusy(false);
        }
    };

    const handleHealthPlusCardClick = async () => {
        if (healthPlusBusy) return;
        if (healthPlusActive) {
            await setHealthPlusActivationState(false);
            return;
        }
        if (healthPlusEntitled) {
            await setHealthPlusActivationState(true);
            return;
        }
        setShowHealthPlusModal(true);
    };

    const handleHealthPlusProceedToPayment = () => {
        setShowHealthPlusModal(false);
        setShowHealthPlusPaymentModal(true);
    };

    const { approveMenu, isApproving } = useMenuApproval({
        showSuccessModal: false, // Handle custom success flow below
        onSuccess: async (options = {}) => {
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.post(`${API_BASE_URL}/api/alakart-menu/commit`, {
                    // [FIX] "Büyük Mutfağım Refactor" bunu false yapıp sepet oluşumunu kırmıştı → true (commit eksikler için liste kurar).
                    createShoppingList: true
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!res.data.success) throw new Error(res.data.error || 'Commit failed');

                await fetchOtonomPlan();
                await fetchPlanStatus();
                
                // 🚀 V36.2: AUTO-TRIGGER COMPLETION MODAL
                // We check if this approval completes the 7-day week (locally or globally)
                // [A.2] Batch/haftalık onay daima week-complete sayılır (stale approvedDays closure'ına
                // güvenmeden) → commit alışveriş modalı garanti gösterilir, tekil gün onayı sessiz kalır.
                const isWeekComplete = options.isBatch === true || (approvedDays.length + 1) >= 7;

                // 🟢 SILENT MODE: Only return early if week is NOT complete
                if (options.skipInteractiveShopping && !isWeekComplete) return;

                const { shareUrl, summary: commitSummary } = res.data;
                const fullLink = buildPublicShareUrl(shareUrl);
                const commitShoppingCount = Number(
                    commitSummary?.shoppingItemsCount
                    ?? commitSummary?.shoppingItems
                    ?? (Array.isArray(commitSummary?.items) ? commitSummary.items.length : 0)
                );
                window.dispatchEvent(new Event('Alakart_SYNC'));
                window.dispatchEvent(new Event('shoppingListUpdated'));
                window.dispatchEvent(new Event('realtime:inventory-updated'));

                Swal.fire({
                    title: isWeekComplete ? 'Haftalık Plan Tamam!' : 'Gün Onaylandı!',
                    html: `
                        <div style="text-align: left; background: rgba(0,0,0,0.05); padding: 15px; border-radius: 12px; margin-bottom: 20px;">
                            <p><b>${commitSummary.reservedItems}</b> malzeme rezerve edildi.</p>
                            <p>${commitShoppingCount > 0 ? `<b>${commitShoppingCount}</b> eksik ürün için alışveriş önerisi hazır.` : 'Her şey hazır!'}</p>
                        </div>
                        ${isWeekComplete ? '<p>Harika bir hafta seni bekliyor! Planı ailenle paylaşmak ister misin?</p>' : '<p>Paylaşım linkini şimdi almak ister misin?</p>'}
                    `,
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Evet, Paylaş',
                    cancelButtonText: 'Kapat',
                    confirmButtonColor: '#10b981'
                }).then((shareResult) => {
                    if (shareResult.isConfirmed) {
                        Swal.fire({
                            title: 'Paylaşım Linki',
                            html: `<code style="text-align: left; display: block; background: #f3f4f6; padding: 10px; border-radius: 8px; word-break: break-all; font-size: 12px;">${fullLink}</code>`,
                            confirmButtonText: 'Linki Kopyala'
                        }).then(() => copyToClipboard(fullLink));
                    }
                    if (commitShoppingCount > 0) {
                        const reminderListId = res.data?.shoppingListId;
                        setTimeout(() => {
                            Swal.fire({
                                title: 'Alışveriş Önerisi Hazır',
                                text: 'Eksik malzemeleri alışveriş listesine eklemek ister misin?',
                                icon: 'info',
                                showCancelButton: true,
                                confirmButtonText: 'Listeyi Aç',
                                cancelButtonText: 'Kapat'
                            }).then((shopRes) => {
                                if (shopRes.isConfirmed) {
                                    navigate('/my-shopping-lists', {
                                        state: {
                                            openListId: reminderListId || null,
                                            openHistory: true
                                        }
                                    });
                                }
                                // Liste hazır → "hatırlatma kuralım mı?" ikinci modalı (push).
                                if (reminderListId) {
                                    promptAndSetShoppingReminder(reminderListId, 'Alakart 1. Hafta', { confirmColor: '#10b981' });
                                }
                            });
                        }, 500);
                    }
                });
            } catch (error) {
                console.error('ERP Commit error:', error);
                Swal.fire({ icon: 'error', title: 'Sistem Hatası', text: 'Plan onaylandı ancak stok rezervasyonu sırasında sorun oluştu.' });
            }
        }
    });

    const handleApproveDay = (dateKey) => {
        if (!otonomPlan || !planDays || Object.keys(planDays).length === 0) return;

        const dayRecipes = [];
        const dayPlan = planDays[dateKey];

        if (dayPlan) {
            Object.values(dayPlan).forEach(slot => {
                if (slot && (slot.id || slot.recipeId || slot.name) && slot !== '_meta') {
                    // 🚀 V38.3: Ensure missingIngredients is passed
                    const recipeWithMissing = {
                        ...slot,
                        id: slot.id || slot.recipeId,
                        name: slot.name || slot.title,
                        missingIngredients: slot.missingIngredients || (slot.intelligence?.missingIngredients || [])
                    };
                    dayRecipes.push(recipeWithMissing);
                }
            });
        }

        if (dayRecipes.length === 0) {
            Swal.fire('Uyarı', 'Onaylanacak hiç yemek yok.', 'warning');
            return;
        }

        if (!approvedDays.includes(dateKey)) {
            setApprovedDays(prev => [...prev, dateKey]);
        }
        approveMenu(dayRecipes, {
            skipInteractiveShopping: true,
            suppressBulkShoppingList: true,
            sourceSurface: 'alakart'
        }); // SILENT check
    };

    // Config & UI State
    const [alakartConfig, setAlakartConfig] = useState(() => {
        const saved = localStorage.getItem('alakartConfig');
        return saved ? JSON.parse(saved) : null;
    });
    const [showConfigScreen, setShowConfigScreen] = useState(false);
    const [showFinalSummary, setShowFinalSummary] = useState(false);
    const [showRatingSummary, setShowRatingSummary] = useState(false);
    const [weeklyPlan, setWeeklyPlan] = useState([]);
    const [consolidatedIngredients, setConsolidatedIngredients] = useState([]);

    useEffect(() => {
        if (location.state?.openRatingSummary || (location.search && new URLSearchParams(location.search).get('openRatingSummary'))) {
            setShowRatingSummary(true);
        }
    }, [location]);

    const generatedDaysList = useMemo(() => {
        const result = [];
        const daysTrLow = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
        const daysTrShort = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

        for (let i = 0; i < 7; i++) {
            const date = DateTime.now().plus({ days: i });
            const dayName = date.setLocale('en-US').toFormat('cccc');
            const dayTr = daysTrLow[date.weekday % 7];
            const dayShort = daysTrShort[date.weekday % 7];
            const dayFull = `${date.toFormat('dd.MM')} ${dayTr.toUpperCase()}`;

            result.push({
                key: dayName,
                date: date.toISODate(),
                label: dayTr,
                short: dayShort,
                full: dayFull,
                isToday: i === 0
            });
        }
        return result;
    }, []);

    const [otonomPlan, setOtonomPlan] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(0); 
    const [alakartExpiresAt, setAlakartExpiresAt] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [showSurvey, setShowSurvey] = useState(false);
    const [planContext, setPlanContext] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [ratingInviteLink, setRatingInviteLink] = useState(null);
    const [showRatingInviteUI, setShowRatingInviteUI] = useState(false);
    const [creatingRatingInvite, setCreatingRatingInvite] = useState(false);
    const [planStatus, setPlanStatus] = useState({ state: 'No Plan' });
    const planDays = useMemo(() => resolvePlanDays(otonomPlan), [otonomPlan]);

    const handleConfigSave = async (nextConfig) => {
        const normalizedConfig = {
            portion: Number(nextConfig?.portion || 4),
            meals: Array.isArray(nextConfig?.meals) ? nextConfig.meals : []
        };

        setAlakartConfig(normalizedConfig);
        setPortion(normalizedConfig.portion);
        setShowConfigScreen(false);
        localStorage.setItem('alakartConfig', JSON.stringify(normalizedConfig));

        try {
            const token = localStorage.getItem('authToken');
            if (token) {
                await axios.post(`${API_BASE_URL}/api/alakart-menu/config`, normalizedConfig, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => null);
            }
        } catch (error) {
            console.error('Alakart config save error:', error);
        }
    };

    const fetchPlanStatus = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setPlanStatus(res.data);
                if (res.data.state === 'dirty' && !loading) {
                    Swal.fire({
                        title: 'Plan Çelişkisi!',
                        text: res.data.reason || 'Plan kısıtlarla veya stokla çelişiyor. Yeniden oluşturmak ister misin?',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Evet, Yeniden Oluştur',
                        confirmButtonColor: '#ef4444'
                    }).then((result) => { if (result.isConfirmed) setShowSurvey(true); });
                }
            }
        } catch (error) { console.error('Status fetch error:', error); }
    };

    const pollGeneratedPlan = async ({ maxAttempts = 20, delayMs = 1500 } = {}) => {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/generate-status`, {
                headers: getAuthHeaders()
            });

            if (res.data?.success && res.data?.status === 'ready' && res.data?.plan) {
                setOtonomPlan(res.data.plan);
                setDraft(syncDraftWithPlan(res.data.plan));
                return res.data.plan;
            }

            if (res.data?.success && res.data?.status === 'failed') {
                const failure = new Error(res.data?.error || 'Plan yeniden üretilemedi');
                failure.details = res.data?.details || null;
                throw failure;
            }

            if (res.data?.success && res.data?.status !== 'generating') {
                throw new Error(`Beklenmeyen generate durumu: ${String(res.data?.status || 'unknown')}`);
            }

            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        throw new Error('Plan yeniden üretimi zamanında tamamlanmadı');
    };

    const handleForceRegeneratePlan = async ({ source = 'manual' } = {}) => {
        try {
            setGenerating(true);
            setOverrideGuidance(null);
            await axios.post(`${API_BASE_URL}/api/alakart-menu/generate`, {
                forceRegenerate: true,
                source
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                }
            });

            await pollGeneratedPlan();
            await fetchPlanStatus();

            Swal.fire({
                icon: 'success',
                title: 'Plan Yeniden Üretildi',
                text: 'Daha dengeli öneriler hazırlandı.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2200
            });
        } catch (error) {
            const payload = {
                error: error?.response?.data?.error || error?.message || 'Şimdilik planı yeniden üretemedik.',
                details: error?.response?.data?.details || error?.details || null
            };
            Swal.fire({
                icon: 'error',
                title: 'Plan Yeniden Üretilemedi',
                html: buildAlakartGenerationErrorHtml(payload)
            });
        } finally {
            setGenerating(false);
        }
    };

    const [approvedDays, setApprovedDays] = useState([]);

    useEffect(() => {
        if (otonomPlan?.id) {
            const savedApproved = localStorage.getItem(`approvedDays_${otonomPlan.id}`);
            setApprovedDays(savedApproved ? JSON.parse(savedApproved) : []);
        } else {
            setApprovedDays([]);
        }
    }, [otonomPlan]);

    useEffect(() => {
        if (otonomPlan?.id) {
            localStorage.setItem(`approvedDays_${otonomPlan.id}`, JSON.stringify(approvedDays));
        }
    }, [approvedDays, otonomPlan]);

    const weeklyMissingItems = useMemo(() => {
        if (!otonomPlan || !planDays || Object.keys(planDays).length === 0) return [];
        const allMissing = [];
        const seen = new Set();
        Object.values(planDays).forEach(daySlots => {
            if (typeof daySlots !== 'object') return;
            Object.values(daySlots).forEach(recipe => {
                if (!recipe || !recipe.missingIngredients) return;
                recipe.missingIngredients.forEach(ing => {
                    const name = (ing.name || ing.ingredientName || '').trim().toLowerCase();
                    if (name && !seen.has(name)) {
                        seen.add(name);
                        allMissing.push({ ...ing, recipeName: recipe.name });
                    }
                });
            });
        });
        return allMissing;
    }, [otonomPlan]);

    // [FIX] EKSİK rozetine tıklayınca NE eksik olduğunu göster (eskiden tıklanabilir değildi).
    const showMissingItems = () => {
        if (!weeklyMissingItems.length) {
            Swal.fire({ icon: 'success', title: 'Eksik yok 🎉', text: 'Bu plandaki tüm malzemeler dolabında mevcut.' });
            return;
        }
        const rows = weeklyMissingItems.map((it) => {
            const name = it.name || it.ingredientName || 'Malzeme';
            const qty = it.quantity ? ` <span style="color:#6b7280">(${it.quantity}${it.unit ? ' ' + it.unit : ''})</span>` : '';
            const from = it.recipeName ? `<div style="font-size:11px;color:#9ca3af">${it.recipeName}</div>` : '';
            return `<li style="padding:8px 4px;border-bottom:1px solid #eee"><b>${name}</b>${qty}${from}</li>`;
        }).join('');
        Swal.fire({
            title: `Eksik Malzemeler (${weeklyMissingItems.length})`,
            html: `<ul style="text-align:left;list-style:none;padding:0;margin:0;max-height:55vh;overflow:auto">${rows}</ul>`,
            confirmButtonText: 'Kapat',
            confirmButtonColor: '#10b981'
        });
    };

    const handleApproveWeek = () => {
        if (!otonomPlan || !planDays || Object.keys(planDays).length === 0) return;
        const allRecipes = [];
        Object.values(planDays).forEach(daySlots => {
            if (typeof daySlots === 'object') {
                Object.values(daySlots).forEach(recipe => {
                    if (recipe && (recipe.id || recipe.recipeId)) allRecipes.push(recipe);
                });
            }
        });
        if (allRecipes.length === 0) {
            Swal.fire('Bilgi', 'Onaylanacak tarif bulunamadı.', 'info');
            return;
        }
        approveMenu(allRecipes, {
            // [A.2 dedup] Eskiden skipInteractiveShopping yoktu → useMenuApproval'ın "Eksik Malzemeler"
            // checkbox modalı, commit'in "Alışveriş Önerisi Hazır" modalıyla ÜST ÜSTE çıkıyordu. Üstelik
            // suppressBulkShoppingList:true olduğundan checkbox seçimleri zaten atılıyordu (vestigial).
            // Tek alışveriş akışı commit tabanlı olan kalsın → checkbox'ı sustur.
            // isBatch:true → haftalık onay daima "week complete" sayılır, commit modalı garanti gösterilir.
            skipInteractiveShopping: true,
            isBatch: true,
            suppressBulkShoppingList: true,
            sourceSurface: 'alakart'
        });
    };

    const [isOverriding, setIsOverriding] = useState(false);
    const [overrideSlot, setOverrideSlot] = useState(null);

    const enrichedDates30 = useMemo(() => {
        const dates = [];
        const start = DateTime.now().startOf('day').plus({ days: 3 });
        const daysTrLow = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
        const daysTrShort = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

        for (let i = 0; i < 30; i++) {
            const date = start.plus({ days: i });
            const dayName = date.setLocale('en-US').toFormat('cccc');
            const dayTr = daysTrLow[date.weekday % 7];
            const dayShort = daysTrShort[date.weekday % 7];
            const dayFull = `${date.toFormat('dd.MM')} ${dayTr.toUpperCase()}`;

            dates.push({
                key: dayName,
                date: date.toISODate(),
                label: dayTr,
                short: dayShort,
                full: dayFull,
                isToday: false // These are future dates by design
            });
        }
        return dates;
    }, []);

    const weekDates = useMemo(() => {
        const startIdx = currentWeek * 7;
        const endIdx = currentWeek === 3 ? enrichedDates30.length : startIdx + 7;
        return enrichedDates30.slice(startIdx, endIdx);
    }, [enrichedDates30, currentWeek]);

    const [activeDate, setActiveDate] = useState(enrichedDates30[0].date);
    const [activeDay, setActiveDay] = useState(enrichedDates30[0].key);
    const [activeCategory, setActiveCategory] = useState('SOUP');
    const [draft, setDraft] = useState({});
    const [overrideGuidance, setOverrideGuidance] = useState(null);
    const currentSlotOverride = useMemo(() => {
        if (!otonomPlan?.userOverrides || !activeDate || !activeCategory) return null;
        return otonomPlan.userOverrides?.[activeDate]?.[activeCategory] || null;
    }, [otonomPlan, activeDate, activeCategory]);
    const userHasOverrides = useMemo(() => {
        const overrideDates = otonomPlan?.userOverrides || {};
        return Object.values(overrideDates).some((slotMap) => Object.keys(slotMap || {}).length > 0);
    }, [otonomPlan]);
    const [recipes, setRecipes] = useState([]);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeModalOpen, setRecipeModalOpen] = useState(false);
    const [ingredientReservations, setIngredientReservations] = useState([]);
    const visibleRecipes = useMemo(() => {
        if (!Array.isArray(recipes)) return [];

        const targetCategory = isOverriding && overrideSlot?.category
            ? overrideSlot.category
            : activeCategory;

        const filtered = recipes.filter((recipe) => {
            if (!targetCategory || targetCategory === 'ALL') return true;
            return resolveRecipeSlotCategory(recipe) === targetCategory;
        });

        if (!overrideGuidance?.metricKey) return filtered;

        return [...filtered].sort((left, right) => {
            const leftPenalty = String(left?.id || '') === String(overrideGuidance?.currentRecipeId || '') ? 100000 : 0;
            const rightPenalty = String(right?.id || '') === String(overrideGuidance?.currentRecipeId || '') ? 100000 : 0;
            const leftMetric = getRecipeMetricValue(left, overrideGuidance.metricKey) + leftPenalty;
            const rightMetric = getRecipeMetricValue(right, overrideGuidance.metricKey) + rightPenalty;

            if (leftMetric !== rightMetric) return leftMetric - rightMetric;

            const leftMatch = Number(left?.match?.matchPercentage || 0);
            const rightMatch = Number(right?.match?.matchPercentage || 0);
            return rightMatch - leftMatch;
        });
    }, [recipes, isOverriding, overrideSlot, activeCategory, overrideGuidance]);

    useEffect(() => {
        if (!otonomPlan) return;

        const consumptionTraceMeta = otonomPlan?.skeleton?._meta?.consumptionBudgetTrace || {};
        const consumptionMetrics = consumptionTraceMeta?.trackedMetrics || {};
        // Faz 4.2: Sağlık+ aktifse eşikler kişiselleştirilmiş — mesajı buna göre zenginleştir.
        const isPersonalizedThreshold = consumptionTraceMeta?.personalized === true;
        const proteinBudgetTrace = otonomPlan?.skeleton?._meta?.proteinBudgetTrace || {};
        const planVersionKey = [
            otonomPlan?.id || 'alakart',
            otonomPlan?.updatedAt || otonomPlan?.createdAt || 'unknown',
            userHasOverrides ? 'user_modified' : 'system_generated'
        ].join('::');
        const fatTrace = consumptionMetrics?.fatG || consumptionMetrics?.oilMl || null;
        const saltTrace = consumptionMetrics?.saltG || null;
        const sugarTrace = consumptionMetrics?.sugarG || null;
        const candidateAlerts = [];

        const pushConsumptionAlert = ({ key, unit, trace, thresholdDefault, contributorLabel, titlePrefix }) => {
            const currentValue = Number(trace?.perPersonPerDay || 0);
            const anomalyAbove = Number(trace?.thresholds?.anomalyAbove || thresholdDefault);
            if (!trace || !Number.isFinite(currentValue) || currentValue <= anomalyAbove) return;
            const contributors = Array.isArray(trace?.breakdown)
                ? [...trace.breakdown]
                    .filter((item) => Number(item?.[key] || 0) > 0)
                    .sort((a, b) => Number(b?.[key] || 0) - Number(a?.[key] || 0))
                    .slice(0, 3)
                : [];
            candidateAlerts.push({
                type: key,
                priority: 100,
                title: userHasOverrides ? 'Plan Dengesi Uyarısı' : 'Önerilen Plan Dengesi Uyarısı',
                textLead: userHasOverrides
                    ? `Seçtiğiniz planda kişi başı günlük ${titlePrefix} ${currentValue.toFixed(1)} ${unit} görünüyor.`
                    : `Önerilen planda kişi başı günlük ${titlePrefix} ${currentValue.toFixed(1)} ${unit} görünüyor.`,
                helperText: userHasOverrides
                    ? 'İstersen yüksek katkı yapan slotlardan birini daha hafif alternatiflerle değiştirebiliriz.'
                    : 'İstersen sistemi bu slotlardan başlayarak daha dengeli alternatiflere yönlendirebiliriz.',
                thresholdText: isPersonalizedThreshold
                    ? `Sağlık+ kişisel eşiğin: ${anomalyAbove} ${unit} (sağlık profiline göre ayarlandı)`
                    : `Anomali eşiği: ${anomalyAbove} ${unit}`,
                contributorLabel,
                contributors,
                confirmButtonText: userHasOverrides ? 'Slotları Gözden Geçir' : 'Planı Yeniden Üret',
                alternativeButtonText: 'Daha Dengeli Alternatifleri Göster'
            });
        };

        pushConsumptionAlert({
            key: 'fatG',
            label: 'yağ',
            unit: 'g',
            trace: fatTrace,
            thresholdDefault: 30,
            contributorLabel: 'Yağı en çok yükselten slotlar',
            titlePrefix: 'zeytinyağı kullanımı'
        });
        pushConsumptionAlert({
            key: 'saltG',
            label: 'tuz',
            unit: 'g',
            trace: saltTrace,
            thresholdDefault: 5,
            contributorLabel: 'Tuzu en çok yükselten slotlar',
            titlePrefix: 'tuz yükü'
        });
        pushConsumptionAlert({
            key: 'sugarG',
            label: 'şeker',
            unit: 'g',
            trace: sugarTrace,
            thresholdDefault: 20,
            contributorLabel: 'Şekeri en çok yükselten slotlar',
            titlePrefix: 'şeker yükü'
        });

        candidateAlerts.forEach((alert) => {
            const isConsumptionAlert = ['fatG', 'saltG', 'sugarG'].includes(alert?.type);
            if (!isConsumptionAlert) return;

            alert.title = userHasOverrides ? 'Plan Dengesi Uyarısı' : 'Önerilen Plan Dengesi Uyarısı';
            alert.confirmButtonText = userHasOverrides ? 'Slotları Gözden Geçir' : 'Planı Yeniden Üret';
            alert.alternativeButtonText = 'Daha Dengeli Alternatifleri Göster';

            if (alert.type === 'fatG') {
                const currentValue = Number(fatTrace?.perPersonPerDay || 0);
                alert.textLead = userHasOverrides
                    ? `Seçtiğiniz planda kişi başı günlük yağ yükü ${currentValue.toFixed(1)} g görünüyor.`
                    : `Önerilen planda kişi başı günlük yağ yükü ${currentValue.toFixed(1)} g görünüyor.`;
                alert.helperText = userHasOverrides
                    ? 'İstersen yüksek katkı yapan slotlardan birini daha hafif alternatiflerle değiştirebiliriz.'
                    : 'İstersen sistemi bu slotlardan başlayarak daha dengeli alternatiflere yönlendirebiliriz.';
                alert.thresholdText = `Anomali eşiği: ${Number(fatTrace?.thresholds?.anomalyAbove || 30)} g`;
                alert.contributorLabel = 'Yağ yükünü en çok yükselten slotlar';
            } else if (alert.type === 'saltG') {
                const currentValue = Number(saltTrace?.perPersonPerDay || 0);
                alert.textLead = userHasOverrides
                    ? `Seçtiğiniz planda kişi başı günlük tuz yükü ${currentValue.toFixed(1)} g görünüyor.`
                    : `Önerilen planda kişi başı günlük tuz yükü ${currentValue.toFixed(1)} g görünüyor.`;
                alert.helperText = userHasOverrides
                    ? 'İstersen yüksek katkı yapan slotlardan birini daha hafif alternatiflerle değiştirebiliriz.'
                    : 'İstersen sistemi bu slotlardan başlayarak daha dengeli alternatiflere yönlendirebiliriz.';
                alert.thresholdText = `Anomali eşiği: ${Number(saltTrace?.thresholds?.anomalyAbove || 5)} g`;
                alert.contributorLabel = 'Tuzu en çok yükselten slotlar';
            } else if (alert.type === 'sugarG') {
                const currentValue = Number(sugarTrace?.perPersonPerDay || 0);
                alert.textLead = userHasOverrides
                    ? `Seçtiğiniz planda kişi başı günlük şeker yükü ${currentValue.toFixed(1)} g görünüyor.`
                    : `Önerilen planda kişi başı günlük şeker yükü ${currentValue.toFixed(1)} g görünüyor.`;
                alert.helperText = userHasOverrides
                    ? 'İstersen yüksek katkı yapan slotlardan birini daha hafif alternatiflerle değiştirebiliriz.'
                    : 'İstersen sistemi bu slotlardan başlayarak daha dengeli alternatiflere yönlendirebiliriz.';
                alert.thresholdText = `Anomali eşiği: ${Number(sugarTrace?.thresholds?.anomalyAbove || 20)} g`;
                alert.contributorLabel = 'Şekeri en çok yükselten slotlar';
            }
        });

        const proteinAlerts = Array.isArray(proteinBudgetTrace?.alerts)
            ? proteinBudgetTrace.alerts.filter((alert) => alert?.severity === 'anomaly')
            : [];
        proteinAlerts.forEach((alert) => {
            const metric = proteinBudgetTrace?.trackedMetrics?.[alert.metricKey];
            const contributors = Array.isArray(metric?.mainOnlyBreakdown)
                ? metric.mainOnlyBreakdown.slice(0, 3)
                : Array.isArray(metric?.breakdown)
                    ? metric.breakdown.slice(0, 3)
                    : [];
            candidateAlerts.push({
                type: alert.metricKey,
                priority: 90,
                title: userHasOverrides ? 'Protein Dengesi Uyarısı' : 'Önerilen Plan Protein Uyarısı',
                textLead: `${alert.headline}`,
                helperText: alert.explain,
                thresholdText: `Hedef üst sınır: ${alert.thresholds?.targetMax ?? '-'} ${alert.unit}`,
                contributorLabel: contributors.length > 0 ? `${alert.label} katkısı yüksek slotlar` : 'Dengelenebilecek ana yemek slotları',
                contributors,
                confirmButtonText: userHasOverrides ? 'Slotları Gözden Geçir' : 'Planı Yeniden Üret',
                alternativeButtonText: 'Daha Dengeli Alternatifleri Göster'
            });
        });

        // Faz 5.3: sistem modu anomaly flag'leri de kandidata ekle
        const systemAnomalies = otonomPlan?.skeleton?._meta?.blockingIssues || [];
        if (otonomPlan?.skeleton?._meta?.hasSystemGeneratedAnomalies && systemAnomalies.length > 0 && !userHasOverrides) {
            const first = systemAnomalies[0];
            candidateAlerts.push({
                type: first.type || 'system_anomaly',
                priority: 110, // consumption/protein üzerinde
                title: 'Önerilen Plan Ayarlanabilir',
                textLead: String(first.message || 'Plan otomatik üretimde bir dengesizlik içeriyor.'),
                helperText: 'Planı yeniden üretebilir veya slotları elle düzenleyebilirsin.',
                thresholdText: `Eşik: ${first.threshold ?? '-'} ${first.unit ?? ''}`,
                contributorLabel: 'Etkileyen slotlar',
                contributors: Array.isArray(first.contributors) ? first.contributors.slice(0, 3) : [],
                confirmButtonText: 'Planı Yeniden Üret',
                alternativeButtonText: 'Elle Düzenle'
            });
        }

        const selectedAlert = [...candidateAlerts].sort((a, b) => b.priority - a.priority)[0];
        if (!selectedAlert) return;
        const dismissedKey = `alakart_anomaly_modal_seen::${planVersionKey}::${selectedAlert.type}`;
        if (sessionStorage.getItem(dismissedKey) === '1') return;
        sessionStorage.setItem(dismissedKey, '1');

        const fallbackMainSlot = Object.entries(planDays || {}).flatMap(([date, slots]) => {
            if (!slots || typeof slots !== 'object') return [];
            return slots?.MAIN ? [{ date, slot: 'MAIN', recipeName: slots?.MAIN?.name || slots?.MAIN?.title || 'Ana Yemek' }] : [];
        })[0] || null;

        const topContributor = selectedAlert.contributors?.[0] || fallbackMainSlot || null;
        const contributorHtml = selectedAlert.contributors?.length > 0
            ? `
                <div style="margin-top:12px;padding:12px 14px;border-radius:14px;background:rgba(15,23,42,0.05);text-align:left;">
                    <div style="font-size:11px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;margin-bottom:8px;">
                        ${selectedAlert.contributorLabel}
                    </div>
                    ${selectedAlert.contributors.map((item) => {
                        const numericContribution = Number(item.fatG || item.oilMl || item.saltG || item.sugarG || 0);
                        const contributionText = numericContribution > 0
                            ? `<strong>${numericContribution.toFixed(0)} ${item.fatG || item.saltG || item.sugarG ? 'g' : 'ml'}</strong>`
                            : `<strong>Slot</strong>`;
                        return `
                        <div style="display:flex;justify-content:space-between;gap:12px;font-size:13px;padding:6px 0;border-bottom:1px solid rgba(148,163,184,0.18);">
                            <span>${item.date} / ${item.slot} / ${item.recipeName || 'Tarif'}</span>
                            ${contributionText}
                        </div>
                    `;
                    }).join('')}
                </div>
            `
            : `
                <div style="margin-top:12px;padding:12px 14px;border-radius:14px;background:rgba(15,23,42,0.05);text-align:left;color:#475569;">
                    ${selectedAlert.contributorLabel}
                </div>
            `;

        Swal.fire({
            icon: 'warning',
            title: selectedAlert.title,
            html: `
                <div style="text-align:left;">
                    <p style="margin:0 0 8px 0;">${selectedAlert.textLead}</p>
                    <p style="margin:0;color:#475569;">${selectedAlert.thresholdText}</p>
                    <p style="margin:10px 0 0 0;color:#475569;">${selectedAlert.helperText}</p>
                    ${contributorHtml}
                </div>
            `,
            showCancelButton: true,
            showDenyButton: !userHasOverrides,
            confirmButtonText: selectedAlert.confirmButtonText,
            denyButtonText: !userHasOverrides ? selectedAlert.alternativeButtonText : undefined,
            cancelButtonText: 'Planla Devam Et',
            confirmButtonColor: '#f97316',
            denyButtonColor: '#0f766e',
            cancelButtonColor: '#64748b',
            customClass: {
                popup: 'rounded-3xl'
            }
        }).then((result) => {
            if (result.isConfirmed && !userHasOverrides) {
                handleForceRegeneratePlan({ source: 'anomaly_modal' });
                return;
            }

            const shouldReviewSlots = result.isDenied || (result.isConfirmed && userHasOverrides);
            if (!shouldReviewSlots || !topContributor?.date || !topContributor?.slot) return;

            const slotDate = String(topContributor.date);
            const slotCategory = String(topContributor.slot);
            const dateIndex = generatedDaysList.findIndex((day) => String(day.date) === slotDate);
            const slotDay = generatedDaysList.find((day) => String(day.date) === slotDate);

            if (dateIndex >= 0) {
                setCurrentWeek(Math.floor(dateIndex / 7));
            }
            if (slotDay?.date) {
                setActiveDate(slotDay.date);
                setActiveDay(slotDay.key);
            }
            setActiveCategory(slotCategory);
            setOverrideSlot({ date: slotDate, category: slotCategory });
            setOverrideGuidance({
                metricKey: selectedAlert.type,
                currentRecipeId: topContributor?.recipeId || null,
                currentRecipeName: topContributor?.recipeName || 'Tarif',
                slotCategory,
                source: result.isDenied ? 'alternatives' : 'review'
            });
            setIsOverriding(true);
        });
    }, [otonomPlan, userHasOverrides, generatedDaysList, planDays]);

    const allCategories = [
        { id: 'ALL', label: 'Tümü', icon: 'apps' },
        { id: 'BREAKFAST', label: 'Kahvaltı', icon: 'breakfast_dining' },
        { id: 'SOUP', label: 'Çorba', icon: 'soup_kitchen' },
        { id: 'MAIN', label: 'Ana Yemek', icon: 'restaurant_menu' },
        { id: 'SALAD', label: 'Salata', icon: 'eco' },
        { id: 'DESSERT', label: 'Tatlı', icon: 'icecream' },
        { id: 'LEGUME', label: 'Bakliyat', icon: 'rebase_edit' },
        { id: 'PASTA', label: 'Makarna/Pilav', icon: 'bakery_dining' },
        { id: 'CHICKEN', label: 'Tavuk', icon: 'kebab_dining' },
        { id: 'MEAT', label: 'Et/Köfte', icon: 'outdoor_grill' },
        { id: 'VEGETABLE', label: 'Sebze', icon: 'psychology_alt' },
        { id: 'SEAFOOD', label: 'Deniz Ürünleri', icon: 'set_meal' },
        { id: 'PASTRY', label: 'Hamur İşi', icon: 'bakery_dining' }
    ];

    const categories = useMemo(() => {
        return alakartConfig
            ? [allCategories[0], ...allCategories.filter(c => c.id !== 'ALL' && alakartConfig.meals.includes(c.id))]
            : allCategories;
    }, [alakartConfig]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.get(`${API_BASE_URL}/api/profile?t=${Date.now()}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const apiData = res.data || {};
                const userData = apiData.user || {};
                const alakartProp = userData.hasAlakartPremium;
                setHasPremium(alakartProp === true || alakartProp === 'true' || alakartProp === 1 || alakartProp === '1');
                setUserProfile(apiData);
                if (userData.alakartExpiresAt) {
                    setAlakartExpiresAt(userData.alakartExpiresAt);
                    setIsExpired(new Date(userData.alakartExpiresAt) < new Date());
                }
                if (apiData.servingSize) setPortion(apiData.servingSize);
                await Promise.all([fetchOtonomPlan(), fetchPlanStatus()]);
            } catch (error) { setHasPremium(false); } finally { setLoading(false); }
        };
        init();
    }, []);

    useEffect(() => {
        if (!loading && (!otonomPlan || !planDays || Object.keys(planDays).length === 0)) setShowSurvey(true);
    }, [loading, otonomPlan]);

    const fetchOtonomPlan = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/plan`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setOtonomPlan(res.data.plan);
                setDraft(syncDraftWithPlan(res.data.plan));
                setPlanContext(res.data.plan?.contextSnapshot || null);
                const nextConfig = normalizeAlakartConfigFromPlan(res.data.plan, portion);
                if (nextConfig) {
                    setAlakartConfig(nextConfig);
                    localStorage.setItem('alakartConfig', JSON.stringify(nextConfig));
                    setPortion(nextConfig.portion);
                }
            }
        } catch (error) { console.error('Plan fetch error:', error); }
    };

    const lunchPreference = userProfile?.onboardingStates?.lunchPreference || null;
    const lunchPlanBadge = useMemo(() => {
        const mode = String(lunchPreference?.mode || '').trim();
        if (!mode) return null;
        if (mode === 'extra_dinner_portion') {
            const extra = lunchPreference?.extraServingSize === 'family_size'
                ? 'aile boyu'
                : `${Number(lunchPreference?.extraServingSize || 0) || ''}`.trim();
            return {
                tone: 'amber',
                title: 'Öğlen akşamdan çözülecek',
                text: extra
                    ? `Akşam slotları ${extra} ek öğlen porsiyonuyla planlanıyor.`
                    : 'Akşam slotları ek öğlen porsiyonuyla planlanıyor.'
            };
        }
        if (mode === 'leftover_only') {
            return {
                tone: 'slate',
                title: 'Öğlen için ayrı plan yok',
                text: 'Bu hafta öğlen için kalanları değerlendirme davranışı korunuyor.'
            };
        }
        if (mode === 'light_practical_lunch') {
            return {
                tone: 'sky',
                title: 'Hafif / pratik öğlen aktif',
                text: 'Öğlen tarafında hafif ve hızlı adaylar bekleniyor; akşam ana plan baskısı artmıyor.'
            };
        }
        if (mode === 'separate_lunch_plan') {
            return {
                tone: 'rose',
                title: 'Bağımsız öğlen planı aktif',
                text: 'Öğlen önerileri akşam planından ayrı çalışacak şekilde kurgulandı.'
            };
        }
        return null;
    }, [lunchPreference]);

    const handleInviteFamily = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/alakart-menu/family-invite`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setInviteLink(res.data.inviteLink);
                setShowInviteModal(true);
            }
        } catch (error) { console.error('Invite error:', error); }
    };

    const fetchRecipes = async () => {
        try {
            setRecipesLoading(true);
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/recipes`, {
                params: { category: activeCategory, portion: portion, isBrowseAll: false },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) setRecipes(res.data.recipes);
        } catch (error) { console.error('Recipe fetch error:', error); } finally { setRecipesLoading(false); }
    };

    useEffect(() => { fetchRecipes(); }, [activeCategory, isOverriding, portion]);

    const handleToggleRecipe = async (recipeId, forcedCategory = null) => {
        if (isExpired) {
            Swal.fire({ icon: 'warning', title: 'Abonelik Gerekli', text: 'Planınızda değişiklik yapmak için aboneliğinizi yenileyin.' });
            return;
        }
        try {
            const token = localStorage.getItem('authToken');
            const targetDate = overrideSlot?.date || activeDate;
            const targetCategory = forcedCategory || overrideSlot?.category || activeCategory;
            const isRemovingSelection = !recipeId;
            const res = await axios.patch(`${API_BASE_URL}/api/alakart-menu/day/${targetDate}`, {
                slot: targetCategory, recipeId
            }, { headers: { Authorization: `Bearer ${token}` } });

            if (res.data.success) {
                setOtonomPlan(res.data.plan);
                setDraft(syncDraftWithPlan(res.data.plan));
                setActiveDate(targetDate);
                setActiveCategory(targetCategory);
                if (isRemovingSelection) {
                    setIsOverriding(true);
                    setOverrideSlot({ date: targetDate, category: targetCategory });
                } else {
                    setIsOverriding(false);
                    setOverrideSlot(null);
                    setOverrideGuidance(null);
                }
                setRecipeModalOpen(false);
                Swal.fire({ icon: 'success', title: 'Plan Güncellendi!', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });

                // [A.5] Eksik-ürün/availability hesabı artık her seçimde DEĞİL, gün tamamlanınca
                // (tüm kategoriler dolunca) ARKA PLANDA tek seferde yapılır. Seçim anlık kalır.
                if (!isRemovingSelection) {
                    const updatedDay = res.data.plan?.generatedDays?.[targetDate]
                        || res.data.plan?.days?.[targetDate]
                        || res.data.plan?.skeleton?._meta?.effectiveDays?.[targetDate];
                    const requiredCats = (alakartConfig?.meals || []).map((c) => String(c).toUpperCase());
                    const dayComplete = requiredCats.length > 0 && updatedDay
                        && requiredCats.every((c) => updatedDay[c] && (updatedDay[c].id || updatedDay[c].recipeId || updatedDay[c].name));
                    if (dayComplete) {
                        axios.patch(`${API_BASE_URL}/api/alakart-menu/day/${targetDate}/batch`, { assignments: [] }, {
                            headers: { Authorization: `Bearer ${token}` }
                        }).then((r) => {
                            if (r.data?.success && r.data.plan) {
                                setOtonomPlan(r.data.plan);
                                setDraft(syncDraftWithPlan(r.data.plan));
                            }
                        }).catch(() => { /* arka plan hesap; sessiz geç */ });
                    }
                }
            }
        } catch (error) { Swal.fire({ icon: 'error', title: 'Hata', text: 'Plan güncellenemedi.' }); }
    };

    const handleRecipeClick = async (recipe) => {
        const hydratedRecipe = await hydrateRecipeForModal(recipe);
        setSelectedRecipe(hydratedRecipe);
        setRecipeModalOpen(true);
        try {
            const token = localStorage.getItem('authToken');
            const ingredients = hydratedRecipe?.malzemeler || hydratedRecipe?.ingredients || recipe.malzemeler || recipe.ingredients || [];
            const dictIds = ingredients.map(ing => typeof ing === 'object' ? (ing.dictionaryId || ing.id) : null).filter(id => id && id !== 'staple');
            if (dictIds.length > 0) {
                const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/reservations/bulk`, {
                    params: { ids: dictIds.join(',') },
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIngredientReservations(res.data.success ? res.data.reservations : []);
            } else { setIngredientReservations([]); }
        } catch (error) { setIngredientReservations([]); }
    };

    const handleResetPlan = async () => {
        const result = await Swal.fire({
            title: 'Planı Sıfırla?',
            text: "Tüm haftalık seçimleriniz ve otonom planınız silinecektir. Bu işlem geri alınamaz!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Evet, Sıfırla',
            cancelButtonText: 'Vazgeç',
            background: isDarkMode ? '#1e293b' : '#fff',
            color: isDarkMode ? '#fff' : '#1e293b'
        });

        if (!result.isConfirmed) return;

        // Yükleme durumu göster
        Swal.fire({
            title: 'Plan Siliniyor...',
            text: 'Veriler güvenli bir şekilde temizleniyor, lütfen bekleyin.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
            background: isDarkMode ? '#1e293b' : '#fff',
            color: isDarkMode ? '#fff' : '#1e293b'
        });

        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/alakart-menu/reset`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                const planId = otonomPlan?.id;
                setOtonomPlan(null);
                setDraft({});
                setApprovedDays([]);
                if (planId) localStorage.removeItem(`approvedDays_${planId}`);
                
                Swal.fire({
                    icon: 'success',
                    title: 'Plan Sıfırlandı',
                    text: 'Yeni planınızı oluşturmaya başlayabilirsiniz.',
                    timer: 1500,
                    showConfirmButton: false,
                    background: isDarkMode ? '#1e293b' : '#fff',
                    color: isDarkMode ? '#fff' : '#1e293b'
                });

                setShowSurvey(true);
            }
        } catch (error) {
            console.error('Reset error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Hata',
                text: 'Plan silinirken bir sorun oluştu.',
                background: isDarkMode ? '#1e293b' : '#fff',
                color: isDarkMode ? '#fff' : '#1e293b'
            });
        }
    };

    const handleApproveAllWeek = async () => {
        const allRecipes = [];
        const allDayKeys = Object.keys(planDays);

        allDayKeys.forEach(dayKey => {
            const dayPlan = planDays[dayKey];
            if (dayPlan && dayKey !== '_meta') {
                Object.values(dayPlan).forEach(slot => {
                    if (slot && (slot.id || slot.recipeId || slot.name)) {
                        const recipeWithMissing = {
                            ...slot,
                            id: slot.id || slot.recipeId,
                            name: slot.name || slot.title,
                            missingIngredients: slot.missingIngredients || (slot.intelligence?.missingCount > 0 ? [{ name: 'Eksik Malzemeler', quantity: 0, unit: '' }] : [])
                        };
                        allRecipes.push(recipeWithMissing);
                    }
                });
            }
        });

        if (allRecipes.length === 0) {
            Swal.fire('Uyarı', 'Onaylanacak hiç yemek yok.', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Haftalık Onay',
            text: `Tüm 7 günlük planı tek seferde dondurmak istediğinize emin misiniz?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Evet, Tümünü Onayla',
            confirmButtonColor: '#10b981'
        });

        if (result.isConfirmed) {
            const allRecipes = [];
            allDayKeys.forEach(dayKey => {
                const dayPlan = planDays[dayKey];
                if (dayPlan && dayKey !== '_meta') {
                    Object.values(dayPlan).forEach(slot => {
                        if (slot && (slot.id || slot.recipeId || slot.name)) {
                            allRecipes.push({
                                ...slot,
                                id: slot.id || slot.recipeId,
                                name: slot.name || slot.title,
                                missingIngredients: slot.missingIngredients || (slot.intelligence?.missingIngredients || [])
                            });
                        }
                    });
                }
            });

            // Update UI immediately (optimistic)
            setApprovedDays(allDayKeys.filter(k => k !== '_meta'));
            approveMenu(allRecipes, {
                // [A.2 dedup] Mükerrer alışveriş modalını önle: useMenuApproval checkbox modalı +
                // commit "Alışveriş Önerisi Hazır" modalı üst üste geliyordu. Tek alışveriş akışı
                // commit tabanlı olan; checkbox susturuldu (seçimler suppressBulkShoppingList ile zaten atılıyordu).
                skipInteractiveShopping: true,
                isBatch: true,
                suppressBulkShoppingList: true,
                sourceSurface: 'alakart'
            });
        }
    };

    // Check if all preferred categories for a day are filled
    const isDayComplete = (dayKey) => {
        if (!alakartConfig || !draft[dayKey]) return false;
        return alakartConfig.meals.every(mealCategory => draft[dayKey][mealCategory]);
    };

    // Check if all 7 days are complete
    const isWeekComplete = () => {
        if (!alakartConfig) return false;
        return generatedDaysList.every(day => isDayComplete(day.key));
    };

    // Consolidate ingredients from all selected recipes
    const consolidateIngredients = async () => {
        const allRecipeIds = [];
        generatedDaysList.forEach(day => {
            if (draft[day.key]) {
                Object.values(draft[day.key]).forEach(recipeId => {
                    if (recipeId) allRecipeIds.push(recipeId);
                });
            }
        });

        if (allRecipeIds.length === 0) return [];

        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/alakart-menu/recipes`, {
                params: { ids: allRecipeIds.join(',') },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.data.success) return [];

            const ingredientMap = {};

            res.data.recipes.forEach(recipe => {
                const ingredients = recipe.malzemeler || recipe.ingredients || [];
                ingredients.forEach(ing => {
                    const name = typeof ing === 'string' ? ing : (ing.ingredient?.name || ing.name || '');
                    const quantity = ing.quantity || ing.miktar || 0;
                    const unit = ing.unit || ing.birim || 'adet';
                    const inStock = ing.inStock !== undefined ? ing.inStock : true;

                    if (!inStock && name) {
                        const key = `${name.toLowerCase()}_${unit.toLowerCase()}`;
                        if (ingredientMap[key]) {
                            ingredientMap[key].quantity += parseFloat(quantity) || 0;
                        } else {
                            ingredientMap[key] = {
                                name,
                                quantity: parseFloat(quantity) || 0,
                                unit
                            };
                        }
                    }
                });
            });

            return Object.values(ingredientMap).map(item => {
                const countUnits = ['adet', 'tane', 'paket', 'bağ', 'demet', 'diş', 'file', 'koli', 'yaprak', 'dal'];
                const shouldRound = countUnits.includes(item.unit?.toLowerCase().trim());

                return {
                    ...item,
                    quantity: shouldRound ? Math.ceil(item.quantity) : parseFloat(item.quantity.toFixed(2))
                };
            });
        } catch (error) {
            console.error('Consolidation error:', error);
            return [];
        }
    };

    // Build weekly plan for final summary
    const buildWeeklyPlan = async () => {
        const plan = [];

        for (const day of generatedDaysList) {
            const dayMeals = [];
            if (draft[day.key]) {
                for (const [category, recipeId] of Object.entries(draft[day.key])) {
                    if (recipeId && daySummaryRecipes.length > 0) {
                        const recipe = daySummaryRecipes.find(r => r.id === recipeId);
                        if (recipe) {
                            dayMeals.push({
                                category,
                                name: recipe.name || recipe.title
                            });
                        }
                    }
                }
            }
            if (dayMeals.length > 0) {
                plan.push({
                    date: day.full,
                    meals: dayMeals
                });
            }
        }

        return plan;
    };

    // Auto-trigger final summary when week is complete
    useEffect(() => {
        const triggerSummary = async () => {
            if (alakartConfig && isWeekComplete() && !showFinalSummary) {
                const plan = await buildWeeklyPlan();
                const ingredients = await consolidateIngredients();
                setWeeklyPlan(plan);
                setConsolidatedIngredients(ingredients);
                setShowFinalSummary(true);
            }
        };
        triggerSummary();
    }, [draft, alakartConfig]);

    const isSlotLocked = !!draft[activeDay]?.[activeCategory];

    const handleEditDay = (dateKey) => {
        const dayPlan = planDays?.[dateKey] || {};
        const preferredSlots = ['MAIN', 'LEGUME', 'PASTA', 'CHICKEN', 'MEAT', 'SEAFOOD', 'VEGETABLE'];
        const mainSlot = preferredSlots.find((slot) => dayPlan?.[slot]) || Object.keys(dayPlan).find((slot) => slot !== '_meta');
        const nextCategory = mainSlot || 'MAIN';

        setActiveDate(dateKey);
        setActiveCategory(nextCategory);
        setOverrideGuidance(null);
        setOverrideSlot({ date: dateKey, category: nextCategory });
        setIsOverriding(true);

        Swal.fire({
            toast: true,
            position: 'center',
            icon: 'info',
            title: 'Düzenleme Modu',
            text: 'Değiştirmek istediğiniz yemeğin üzerine tıklamanız yeterlidir.',
            showConfirmButton: false,
            timer: 3000
        });
    };



    const handleRemoveSlot = async (dateKey, slotId) => {
        try {
            const token = localStorage.getItem('authToken');

            // Optimistically update local state first
            if (otonomPlan) {
                const newPlan = { ...otonomPlan };
                const optimisticDays = resolvePlanDays(newPlan);
                if (optimisticDays[dateKey]) {
                    if (newPlan.effectiveDays?.[dateKey]) {
                        delete newPlan.effectiveDays[dateKey][slotId];
                    }
                    if (newPlan.generatedDays?.[dateKey]) {
                        delete newPlan.generatedDays[dateKey][slotId];
                    }
                    setOtonomPlan({ ...newPlan });
                }
            }

            const res = await axios.patch(`${API_BASE_URL}/api/alakart-menu/day/${dateKey}`, {
                slot: slotId,
                recipeId: null // Sending null implies removal/clearing
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                setOtonomPlan(res.data.plan);
                setDraft(syncDraftWithPlan(res.data.plan));
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Yemek Kaldırıldı',
                    showConfirmButton: false,
                    timer: 1000
                });
            }
        } catch (error) {
            console.error('Remove slot error:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Silinemedi',
                showConfirmButton: false,
                timer: 1500
            });
        }
    };

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white dark:bg-[#1a1a1a]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
            </div>
        );
    }

    if (!hasPremium) {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="max-w-4xl mx-auto py-20 px-6">
                    <PremiumLockComponent
                        title="Alakart Menu"
                        message="Haftalik premium menu kurgusunu acin, ailenize gore daha derin planlar yapin."
                        paymentModuleKey="alakart"
                        paymentModuleTitle="Alakart Menu"
                    />
                </div>
            </DashboardLayout>
        );
    }

    // Gatekeeper: Show config screen if not configured or explicitly requested
    if (showConfigScreen || !alakartConfig) {
        return <AlakartConfig onSave={handleConfigSave} isDarkMode={isDarkMode} />;
    }

    // 🆕 V38: Global Intelligence Bar Component
    const AlakartIntelligenceBar = ({ proteinSummary, stockCoverage, consumptionBudgetTrace, proteinBudgetTrace, weeklyMainProteinSkeleton, hasUserOverrides }) => {
        if (!proteinSummary && !stockCoverage && !consumptionBudgetTrace && !proteinBudgetTrace) return null;

        const normalizedSummary = Array.isArray(proteinSummary)
            ? proteinSummary.reduce((acc, item) => {
                if (item?.type) acc[item.type] = item;
                return acc;
            }, {})
            : (proteinSummary || {});

        const fatTrace = consumptionBudgetTrace?.trackedMetrics?.fatG || consumptionBudgetTrace?.trackedMetrics?.oilMl || null;
        const fatPerPersonPerDay = Number(fatTrace?.perPersonPerDay || 0);
        const fatTotal = Number(fatTrace?.total || 0);
        const saltTrace = consumptionBudgetTrace?.trackedMetrics?.saltG || null;
        const saltPerPersonPerDay = Number(saltTrace?.perPersonPerDay || 0);
        const saltTotal = Number(saltTrace?.total || 0);
        const saltThresholds = saltTrace?.thresholds || {};
        const saltWarningAbove = Number(saltThresholds.warningAbove || 4);
        const saltAnomalyAbove = Number(saltThresholds.anomalyAbove || 5);
        const showSaltWarning = Number.isFinite(saltPerPersonPerDay) && saltPerPersonPerDay > saltWarningAbove;
        const isSaltAnomaly = showSaltWarning && saltPerPersonPerDay > saltAnomalyAbove;
        const sugarTrace = consumptionBudgetTrace?.trackedMetrics?.sugarG || null;
        const sugarPerPersonPerDay = Number(sugarTrace?.perPersonPerDay || 0);
        const sugarTotal = Number(sugarTrace?.total || 0);
        const sugarThresholds = sugarTrace?.thresholds || {};
        const sugarWarningAbove = Number(sugarThresholds.warningAbove || 15);
        const sugarAnomalyAbove = Number(sugarThresholds.anomalyAbove || 20);
        const showSugarWarning = Number.isFinite(sugarPerPersonPerDay) && sugarPerPersonPerDay > sugarWarningAbove;
        const isSugarAnomaly = showSugarWarning && sugarPerPersonPerDay > sugarAnomalyAbove;
        const fatThresholds = fatTrace?.thresholds || {};
        const fatWarningAbove = Number(fatThresholds.warningAbove || 25);
        const fatAnomalyAbove = Number(fatThresholds.anomalyAbove || 30);
        const showFatWarning = Number.isFinite(fatPerPersonPerDay) && fatPerPersonPerDay > fatWarningAbove;
        const isFatAnomaly = showFatWarning && fatPerPersonPerDay > fatAnomalyAbove;
        const oilTotal = fatTotal;
        const oilWarningAbove = fatWarningAbove;
        const oilAnomalyAbove = fatAnomalyAbove;
        const showOilWarning = showFatWarning;
        const isOilAnomaly = isFatAnomaly;
        const oilCardClass = isFatAnomaly
            ? 'bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-900/50'
            : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50';
        const oilBadgeClass = isFatAnomaly
            ? 'text-rose-700 bg-rose-100 dark:text-rose-200 dark:bg-rose-900/40'
            : 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40';
        const oilHeadline = hasUserOverrides
            ? 'Seçtiğiniz planda yağ yükü biraz yükseliyor'
            : 'Başlangıç menüsünde yağ yükü biraz yükseliyor';
        const oilExplain = hasUserOverrides
            ? 'İstersen daha hafif bir salata veya ana yemekle bunu birlikte dengeleyebiliriz.'
            : 'Bu başlangıç önerisini daha hafif alternatiflerle sana göre yeniden ayarlayabiliriz.';
        const supplementalBudgetCards = [
            showSaltWarning
                ? {
                    key: 'salt',
                    icon: 'salt',
                    badge: isSaltAnomaly ? 'Anomali' : 'Uyarı',
                    badgeClass: isSaltAnomaly
                        ? 'text-rose-700 bg-rose-100 dark:text-rose-200 dark:bg-rose-900/40'
                        : 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40',
                    cardClass: isSaltAnomaly
                        ? 'bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-900/50'
                        : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50',
                    headline: hasUserOverrides
                        ? 'Seçtiğiniz planda tuz yükü biraz yükseliyor'
                        : 'Başlangıç menüsünde tuz yükü biraz yükseliyor',
                    explain: hasUserOverrides
                        ? 'İstersen daha hafif seçeneklerle tuz dengesini birlikte toparlayabiliriz.'
                        : 'Bu başlangıç önerisini daha düşük tuzlu alternatiflerle yeniden ayarlayabiliriz.',
                    perPersonPerDay: saltPerPersonPerDay,
                    total: saltTotal,
                    unit: 'g',
                    warningAbove: saltWarningAbove,
                    anomalyAbove: saltAnomalyAbove
                }
                : null,
            showSugarWarning
                ? {
                    key: 'sugar',
                    icon: 'cake',
                    badge: isSugarAnomaly ? 'Anomali' : 'Uyarı',
                    badgeClass: isSugarAnomaly
                        ? 'text-rose-700 bg-rose-100 dark:text-rose-200 dark:bg-rose-900/40'
                        : 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40',
                    cardClass: isSugarAnomaly
                        ? 'bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-900/50'
                        : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50',
                    headline: hasUserOverrides
                        ? 'Seçtiğiniz planda şeker yükü biraz yükseliyor'
                        : 'Başlangıç menüsünde şeker yükü biraz yükseliyor',
                    explain: hasUserOverrides
                        ? 'İstersen daha dengeli alternatiflerle bunu birlikte azaltabiliriz.'
                        : 'Bu başlangıç önerisini daha düşük şekerli tariflerle yeniden ayarlayabiliriz.',
                    perPersonPerDay: sugarPerPersonPerDay,
                    total: sugarTotal,
                    unit: 'g',
                    warningAbove: sugarWarningAbove,
                    anomalyAbove: sugarAnomalyAbove
                }
                : null
        ].filter(Boolean);
        const proteinAlertCards = (proteinBudgetTrace?.alerts || []).map((alert) => {
            const isHighSeverity = alert.severity === 'anomaly' || alert.severity === 'warning';
            return {
                key: `protein-${alert.metricKey}`,
                icon: isHighSeverity ? 'warning' : 'info',
                badge: alert.severity === 'anomaly' ? 'Anomali' : alert.severity === 'warning' ? 'Uyarı' : 'Bilgi',
                badgeClass: alert.severity === 'anomaly'
                    ? 'text-rose-700 bg-rose-100 dark:text-rose-200 dark:bg-rose-900/40'
                    : alert.severity === 'warning'
                        ? 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40'
                        : 'text-sky-700 bg-sky-100 dark:text-sky-200 dark:bg-sky-900/40',
                cardClass: alert.severity === 'anomaly'
                    ? 'bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-900/50'
                    : alert.severity === 'warning'
                        ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50'
                        : 'bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-900/50',
                headline: alert.headline,
                explain: alert.explain,
                value: `${alert.total} ${alert.unit}`,
                thresholdText: isHighSeverity
                    ? `Hedef üst sınır: ${alert.thresholds?.targetMax ?? '-'} ${alert.unit}`
                    : `Hedef alt sınır: ${alert.thresholds?.targetMin ?? '-'} ${alert.unit}`
            };
        });

        const proteinOrder = ['BALIK', 'TAVUK', 'ET', 'BAKLİYAT'];
        const proteinIcons = { BALIK: 'phishing', TAVUK: 'egg', ET: 'restaurant', BAKLİYAT: 'spa' };
        const proteinColors = { BALIK: '#0ea5e9', TAVUK: '#f59e0b', ET: '#ef4444', BAKLİYAT: '#10b981' };
        const proteinLabels = { BALIK: 'Balık', TAVUK: 'Tavuk', ET: 'Et', BAKLİYAT: 'Bakliyat' };
        const energyLabels = {
            LIGHT: 'hafif',
            NORMAL: 'normal',
            HEAVY: 'ağır',
            FAST: 'hızlı',
            ECONOMIC: 'ekonomik',
            POT: 'tencere',
            RECOVERY: 'kurtarma'
        };
        const skeletonSummary = weeklyMainProteinSkeleton?.summary || {};
        const skeletonTargets = weeklyMainProteinSkeleton?.targets || {};
        const weeklyEnergyProfile = weeklyMainProteinSkeleton?.weeklyEnergyProfile || {};
        const skeletonCompliance = proteinBudgetTrace?.skeletonCompliance || {};
        const slotThemeNoise = proteinBudgetTrace?.slotThemeNoise || {};
        const skeletonSummaryText = proteinOrder
            .map((type) => {
                const count = Number(skeletonSummary?.[type] || 0);
                if (!count) return null;
                return `${count} gün ${String(proteinLabels[type] || type).toLocaleLowerCase('tr-TR')}`;
            })
            .filter(Boolean)
            .join(', ');
        const energySummaryText = Object.entries(weeklyEnergyProfile || {})
            .filter(([, count]) => Number(count || 0) > 0)
            .map(([key, count]) => `${count} ${energyLabels[key] || String(key || '').toLocaleLowerCase('tr-TR')} gün`)
            .join(', ');
        const slotThemeNoiseText = Number(slotThemeNoise?.animalSupportSlotCount || 0) > 0
            ? `Çorba ve salata tarafındaki ${Number(slotThemeNoise.animalSupportSlotCount || 0)} hayvansal yan slotu ana protein sayımına katmadık; sadece haftalık ritmi izlemek için ayrı takip ediyoruz.`
            : 'Çorba ve salata tarafındaki hayvansal temalar ana protein sayımına yazılmadı; sadece haftalık ritmi bozmamaları için ayrı izleniyor.';
        const skeletonDeviationText = Array.isArray(skeletonCompliance?.deviations) && skeletonCompliance.deviations.length > 0
            ? `Bu öneri hedef omurgaya en yakın noktaya kuruldu; ${skeletonCompliance.deviations.length} küçük dağılım sapması ayrıca izlendi.`
            : 'Başlangıç önerisi haftalık protein omurgasıyla uyumlu görünüyor.';
        const budgetCards = [
            showFatWarning
                ? {
                    key: 'fat',
                    icon: 'local_fire_department',
                    badge: isOilAnomaly ? 'Anomali' : 'Uyarı',
                    badgeClass: oilBadgeClass,
                    cardClass: oilCardClass,
                    headline: oilHeadline,
                    explain: oilExplain,
                    value: `${fatPerPersonPerDay.toFixed(1)} g`,
                    metaLabel: 'kişi başı / gün',
                    thresholdText: `Haftalık toplam: ${oilTotal.toFixed(0)} ml · Eşik: ${oilWarningAbove}-${oilAnomalyAbove} ml`
                }
                : null,
            ...supplementalBudgetCards.map(card => ({
                ...card,
                value: `${card.perPersonPerDay.toFixed(1)} ${card.unit}`,
                metaLabel: 'kişi başı / gün',
                thresholdText: `Haftalık toplam: ${card.total.toFixed(0)} ${card.unit} · Eşik: ${card.warningAbove}-${card.anomalyAbove} ${card.unit}`
            })),
            ...proteinAlertCards.map(card => ({
                ...card,
                metaLabel: 'haftalık toplam'
            }))
        ].filter(Boolean);
        const primaryInsightCard = budgetCards[0] || null;
        const secondaryInsightCards = budgetCards.slice(1);
        const gridClass = secondaryInsightCards.length > 0 ? 'xl:grid-cols-3' : 'xl:grid-cols-2';
        const guidanceItems = [
            skeletonSummaryText
                ? `Haftalık protein omurgasını ${skeletonSummaryText} olacak şekilde yaymaya çalıştık.`
                : `Protein dağılımını haftaya yaymaya çalıştık: balık, tavuk, et ve bakliyat günleri birbirini tamamlasın istedik.`,
            energySummaryText
                ? `Haftalık ritmi ${energySummaryText} akışıyla kurduk; bazı günleri hafif, bazı günleri daha pratik tuttuk.`
                : `Haftalık ritimde hafif, pratik ve ekonomik gün hissini birlikte korumaya çalıştık.`,
            `Yağ, tuz ve şeker yükünü kişi başı / gün bazında kontrol ettik${showFatWarning || showSaltWarning || showSugarWarning ? ' ve yükselen alanları işaretledik' : ''}.`,
            `Stoktaki ürünleri, tekrar riskini ve haftalık akış hissini birlikte düşünerek bu başlangıç menüsünü ayarladık.`,
            slotThemeNoiseText,
            skeletonDeviationText
        ];
        const guidanceThresholds = [
            `Kişi başı / gün bazında`,
            `Yağ hedefi: ${fatWarningAbove}-${fatAnomalyAbove} g`,
            `Tuz hedefi: ${saltWarningAbove}-${saltAnomalyAbove} g`,
            `Şeker hedefi: ${sugarWarningAbove}-${sugarAnomalyAbove} g`
        ];

        return (
            <div className="space-y-4">
                {/* [A.6] 2 kolon: SOL protein dağılımı (sağlık özeti), SAĞ aynı temayı anlatan insight kartları ALT ALTA */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    {/* SOL: Haftalık Protein Dağılımı */}
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Haftalık Protein Dağılımı</h4>
                                <div className="flex gap-2">
                                    {proteinOrder.map(type => (
                                        <div key={type} className="flex flex-col items-center gap-1">
                                            <div
                                                className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold"
                                                style={{ backgroundColor: proteinColors[type] || '#ccc' }}
                                            >
                                                <span className="material-symbols-outlined text-sm">{proteinIcons[type] || 'nutrition'}</span>
                                            </div>
                                            <span className="text-[10px] font-bold text-slate-500">{proteinLabels[type]}</span>
                                            <span className="text-[10px] font-black text-slate-700 dark:text-slate-200">
                                                {normalizedSummary[type]?.current || 0}/{skeletonTargets?.[type]?.max || normalizedSummary[type]?.target || 0}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-2xl font-black text-slate-900 dark:text-white leading-none">%{stockCoverage || 0}</div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase mt-1">Stok Verimi</div>
                            </div>
                        </div>
                    </div>

                    {/* SAĞ: insight kartları alt alta (hepsi aynı temayı anlatıyor) */}
                    <div className="flex flex-col gap-3">
                        {budgetCards.map(card => (
                            <div key={card.key} className={`rounded-3xl p-4 border shadow-sm ${card.cardClass}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="material-symbols-outlined text-lg text-amber-500">{card.icon}</span>
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${card.badgeClass}`}>
                                                {card.badge}
                                            </span>
                                        </div>
                                        <h4 className="text-sm font-black text-slate-900 dark:text-white mb-1">
                                            {card.headline}
                                        </h4>
                                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                            {card.explain}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-2xl font-black text-slate-900 dark:text-white leading-none">
                                            {card.value}
                                        </div>
                                        <div className="text-[10px] font-bold text-slate-500 uppercase mt-1">
                                            {card.metaLabel}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 text-xs text-slate-600 dark:text-slate-300">
                                    {card.thresholdText}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-3xl border border-sky-100 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="material-symbols-outlined text-lg text-sky-500">psychology</span>
                        <span className="px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-sky-700 bg-sky-100 dark:text-sky-200 dark:bg-sky-900/40">
                            Nasıl Ayarlandı
                        </span>
                    </div>
                    <h4 className="text-sm font-black text-slate-900 dark:text-white mb-2">
                        Bu başlangıç menüsünü mutfağına göre şekillendirdik
                    </h4>
                    <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
                        Bu ekran son karar değil, sana göre hazırlanmış bir başlangıç önerisi. İstersen günleri değiştir, sistem de yeni dengeye göre yeniden uyum sağlasın.
                    </p>
                    <div className="space-y-2">
                        {guidanceItems.map((item) => (
                            <div key={item} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
                                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
                                <span>{item}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {guidanceThresholds.map((item) => (
                            <span
                                key={item}
                                className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-700/70 dark:text-slate-200"
                            >
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            <div className="max-w-[1400px] mx-auto p-4 md:p-8 pb-32">
                {generating ? (
                    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-4">
                        <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-slate-900/95 p-8 text-center shadow-2xl">
                            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
                                <div className="h-8 w-8 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                            </div>
                            <h3 className="text-2xl font-black text-white">Plan Yeniden Üretiliyor</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-300">
                                Aynı slotları daha dengeli alternatiflerle yeniden kuruyoruz. Bu ekranda bekleyebilirsin; hazır olunca plan otomatik güncellenecek.
                            </p>
                        </div>
                    </div>
                ) : null}

                {/* Custom Styles Injection */}
                <style>{mobileStyles}</style>

                <div className="mb-6">
                    <HealthPlusMenuCard
                        isActive={healthPlusActive}
                        inactiveButtonLabel={healthPlusEntitled ? 'Sağlık+ başlat' : 'Sağlık+ aktivasyonu'}
                        onClick={handleHealthPlusCardClick}
                    />
                </div>

                <HealthMedicalNotice
                    enabled={healthPlusActive}
                    targetMemberKey="household"
                    className="mb-6"
                />
                {/* Header - Mobile vs Desktop */}
                <header className="mb-4 md:mb-6 flex flex-col md:flex-row justify-between items-start md:items-start gap-4">
                    {/* Mobile Header Design */}
                    <div className="md:hidden pt-2">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-1">
                            <span className="material-symbols-outlined text-sm">location_on</span>
                            Mutfak Envanteri
                        </div>
                        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                            Haftalık Planım
                        </h1>

                        {lunchPlanBadge ? (
                            <div className={`mt-3 rounded-2xl border px-4 py-3 ${
                                lunchPlanBadge.tone === 'amber'
                                    ? 'border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-900/10'
                                    : lunchPlanBadge.tone === 'rose'
                                        ? 'border-rose-200 bg-rose-50 dark:border-rose-900/30 dark:bg-rose-900/10'
                                        : lunchPlanBadge.tone === 'sky'
                                            ? 'border-sky-200 bg-sky-50 dark:border-sky-900/30 dark:bg-sky-900/10'
                                            : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/30'
                            }`}>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300">Öğlen Modu</p>
                                <p className="mt-1 text-sm font-black text-slate-900 dark:text-white">{lunchPlanBadge.title}</p>
                                <p className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300">{lunchPlanBadge.text}</p>
                            </div>
                        ) : null}

                        <div className="mt-4">
                            <AlakartIntelligenceBar 
                                proteinSummary={otonomPlan?.skeleton?._meta?.proteinSummary}
                                stockCoverage={otonomPlan?.skeleton?._meta?.stockCoverage}
                                consumptionBudgetTrace={otonomPlan?.skeleton?._meta?.consumptionBudgetTrace}
                                proteinBudgetTrace={otonomPlan?.skeleton?._meta?.proteinBudgetTrace}
                                weeklyMainProteinSkeleton={otonomPlan?.skeleton?._meta?.weeklyMainProteinSkeleton}
                                hasUserOverrides={userHasOverrides}
                            />
                        </div>

                        {/* 🆕 Weekly Missing Items Widget (Mobile) */}
                        <AnimatePresence>
                            {otonomPlan && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4 p-4 bg-primary/5 border border-primary/10 rounded-2xl flex items-center justify-between"
                                >
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black text-primary uppercase tracking-widest">HAFTALIK EKSİKLER</span>
                                        <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{weeklyMissingItems.length} Malzeme Bulundu</span>
                                    </div>
                                    <button
                                        onClick={handleApproveWeek}
                                        disabled={isApproving}
                                        className="bg-primary text-white p-2.5 px-4 rounded-xl font-bold text-xs shadow-md active:scale-95 transition-transform flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-sm">task_alt</span>
                                        ONAYLA
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {otonomPlan ? (
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setShowRatingSummary(true)}
                                    className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-100 px-3 py-2 text-xs font-black text-indigo-700 transition active:scale-95 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
                                >
                                    <span className="material-symbols-outlined text-base">bar_chart</span>
                                    Sonuçlar
                                </button>
                                <button
                                    onClick={handleResetPlan}
                                    className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-100 px-3 py-2 text-xs font-black text-red-700 transition active:scale-95 dark:border-red-900/30 dark:bg-red-900/20 dark:text-red-300"
                                    title="Tüm Planı Sıfırla"
                                >
                                    <span className="material-symbols-outlined text-base">delete</span>
                                    Planı Sıfırla
                                </button>
                            </div>
                        ) : null}
                    </div>

                    {/* Desktop Header Design */}
                    <div className="hidden md:block">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="material-symbols-outlined text-amber-500 text-sm">workspace_premium</span>
                            <span className="text-xs font-black tracking-widest uppercase text-slate-400 dark:text-slate-500 font-display">ALAKART PLANLAYICI</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-3 font-display tracking-tight">
                            Haftalık <span className="text-primary italic font-black">Olasılık</span> Haritası
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 max-w-2xl leading-relaxed font-medium">
                            Mutfaktaki envanterinizi en verimli yoldan lezzete dönüştürmek için bugün başlayarak planınızı yapalım.
                        </p>
                        {lunchPlanBadge ? (
                            <div className={`mt-4 inline-flex max-w-2xl flex-col rounded-2xl border px-4 py-3 ${
                                lunchPlanBadge.tone === 'amber'
                                    ? 'border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-900/10'
                                    : lunchPlanBadge.tone === 'rose'
                                        ? 'border-rose-200 bg-rose-50 dark:border-rose-900/30 dark:bg-rose-900/10'
                                        : lunchPlanBadge.tone === 'sky'
                                            ? 'border-sky-200 bg-sky-50 dark:border-sky-900/30 dark:bg-sky-900/10'
                                            : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/30'
                            }`}>
                                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500 dark:text-slate-300">Öğlen Modu</span>
                                <span className="mt-1 text-sm font-black text-slate-900 dark:text-white">{lunchPlanBadge.title}</span>
                                <span className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300">{lunchPlanBadge.text}</span>
                            </div>
                        ) : null}

                    </div>

                    {/* Desktop Action Buttons */}
                    <div className="hidden md:flex items-start gap-4 shrink-0">

                        {otonomPlan && (
                            /* Week Buttons */
                            <div className="flex bg-slate-100 dark:bg-slate-900 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 self-end">
                                {[0, 1, 2, 3].map(w => (
                                    <button
                                        key={w}
                                        onClick={() => setCurrentWeek(w)}
                                        className={`px-4 py-2 rounded-xl text-[10px] font-black transition-all ${currentWeek === w
                                            ? 'bg-primary text-white shadow-lg'
                                            : 'text-slate-400 hover:text-slate-600'
                                            }`}
                                    >
                                        {w + 1}. HAFTA
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Right Side Actions Column */}
                        <div className="flex flex-col items-end gap-2">

                            {/* 🆕 Weekly Missing Items Widget (Moved Above Reset) */}
                            {otonomPlan && (
                                <div className="flex items-center gap-4 bg-white dark:bg-slate-800 p-2 pl-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm w-full justify-between">
                                    <button
                                        type="button"
                                        onClick={showMissingItems}
                                        title="Eksik malzemeleri gör"
                                        className="flex flex-col items-start text-left rounded-lg px-1 -mx-1 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                                            <span className="material-symbols-outlined text-xs">shopping_basket</span>
                                            EKSİK
                                            <span className="material-symbols-outlined text-[11px] opacity-60">info</span>
                                        </div>
                                        <div className="text-sm font-bold text-slate-700 dark:text-white underline decoration-dotted decoration-slate-400 underline-offset-2">
                                            {weeklyMissingItems.length} Ürün
                                        </div>
                                    </button>
                                    <button
                                        onClick={handleApproveWeek}
                                        disabled={isApproving}
                                        className={`px-3 py-1.5 ${isApproving ? 'bg-slate-300' : 'bg-primary'} text-white rounded-lg font-bold text-[10px] shadow-sm hover:scale-105 active:scale-95 transition-all flex items-center gap-1`}
                                    >
                                        {isApproving ? (
                                            <div className="w-3 h-3 border-2 border-white border-t-transparent animate-spin rounded-full" />
                                        ) : (
                                            <span className="material-symbols-outlined text-sm">verified_user</span>
                                        )}
                                        ONAYLA
                                    </button>
                                </div>
                            )}

                            {/* Button Row: Rating, Reset, Config */}
                            <div className="flex gap-2">
                                {otonomPlan && (
                                    <button
                                        onClick={() => setShowRatingSummary(true)}
                                        className="flex items-center gap-2 px-4 py-3 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 rounded-xl font-bold transition-all border border-indigo-200 dark:border-indigo-800"
                                    >
                                        <span className="material-symbols-outlined">bar_chart</span>
                                        Sonuçlar
                                    </button>
                                )}

                                <button
                                    onClick={handleResetPlan}
                                    className="flex items-center gap-2 px-4 py-3 bg-red-100 dark:bg-red-900/20 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/40 rounded-xl font-bold transition-all border border-red-200 dark:border-red-900/30"
                                    title="Tüm Planı Sıfırla"
                                >
                                    <span className="material-symbols-outlined">delete</span>
                                </button>

                                {!otonomPlan && (
                                    <button
                                        onClick={() => setShowConfigScreen(true)}
                                        className="flex items-center gap-2 px-6 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black shadow-xl hover:scale-105 transition-transform"
                                    >
                                        <span className="material-symbols-outlined text-xl">settings</span>
                                        Ayarlar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <div className="hidden md:block mb-8">
                        <AlakartIntelligenceBar
                            proteinSummary={otonomPlan?.skeleton?._meta?.proteinSummary}
                            stockCoverage={otonomPlan?.skeleton?._meta?.stockCoverage}
                            consumptionBudgetTrace={otonomPlan?.skeleton?._meta?.consumptionBudgetTrace}
                            proteinBudgetTrace={otonomPlan?.skeleton?._meta?.proteinBudgetTrace}
                            weeklyMainProteinSkeleton={otonomPlan?.skeleton?._meta?.weeklyMainProteinSkeleton}
                            hasUserOverrides={userHasOverrides}
                        />
                </div>



                <div className="mt-4 md:mt-8">
                    {!otonomPlan || !generatedDaysList || generatedDaysList.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 px-6 bg-white dark:bg-slate-800 rounded-[3rem] border-2 border-dashed border-slate-100 dark:border-slate-700 shadow-sm">
                            <div className="w-24 h-24 bg-primary/10 rounded-[2.5rem] flex items-center justify-center text-primary mb-8 animate-pulse">
                                <span className="material-symbols-outlined text-5xl font-black">robot_2</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white text-center mb-4 font-display italic">
                                Alakart Otonom'a Hoş Geldin!
                            </h2>
                            <p className="text-slate-500 dark:text-slate-400 text-center max-w-lg mb-12 font-medium leading-relaxed">
                                Mutfak envanterine, tercihlerine ve tarihçene göre 30 günlük kusursuz bir yemek haritası çıkarmaya hazır mısın?
                            </p>

                            {/* 🆕 V38: Removed duplicate Global Intelligence Bar (Keep only header version) */}

                            {/* Plan varsa ve veri varsa normal akış */}
                            {otonomPlan && generatedDaysList.length > 0 ? (
                                // ... normal akış (this part is not in the provided snippet, assuming it's handled elsewhere or this block is for the "no plan" state)
                                // This comment block is from the instruction, but the context implies this is the "no plan" state.
                                // The instruction is to replace the button with the following block.
                                null // This will be replaced by the actual content for the "no plan" state
                            ) : (
                                // Plan yoksa MODAL otomatik açılsın diye useEffect ekleyeceğiz.
                                // Burada sadece boş placeholder gösterelim.
                                <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-slate-50 dark:bg-slate-900 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                                    <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                                        <span className="text-4xl">🍽️</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Planınız Hazırlanıyor...</h3>
                                    <button onClick={() => setShowSurvey(true)} className="text-primary font-bold hover:underline">
                                        Pencere açılmadı mı? Buraya tıklayın.
                                    </button>
                                </div>
                            )}

                            <button
                                onClick={handleInviteFamily}
                                className="mt-8 flex items-center gap-2 text-slate-400 hover:text-slate-600 font-bold text-sm transition-colors"
                            >
                                <span className="material-symbols-outlined text-lg">group_add</span>
                                Hane Halkını Davet Et (Plan Ortaklığı)
                            </button>
                        </div>
                    ) : isOverriding && overrideSlot ? (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-slate-100 dark:border-slate-800">
                                <div>
                                    <button
                                        onClick={() => {
                                            setIsOverriding(false);
                                            setOverrideGuidance(null);
                                        }}
                                        className="flex items-center gap-2 text-primary font-black mb-3 hover:translate-x-[-4px] transition-transform text-xs uppercase tracking-widest"
                                    >
                                        <span className="material-symbols-outlined text-sm">arrow_back</span>
                                        Planıma Dön
                                    </button>
                                    <h2 className="text-3xl font-black text-slate-900 dark:text-white font-display">
                                        {DateTime.fromISO(overrideSlot.date).setLocale('tr').toFormat('dd MMMM')} <span className="text-primary italic">Seçimi</span>
                                    </h2>
                                    <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[0.2em] mt-2 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-primary" />
                                        {categories.find(c => c.id === overrideSlot.category)?.label} Kategorisi Değiştiriliyor
                                    </p>
                                    {overrideGuidance ? (
                                        <div className="mt-4 inline-flex flex-col gap-2 rounded-2xl border border-emerald-200/60 bg-emerald-50/70 px-4 py-3 text-left dark:border-emerald-900/50 dark:bg-emerald-950/20">
                                            <span className="text-[10px] font-black uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-300">
                                                Daha Dengeli Alternatifler
                                            </span>
                                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                                                {overrideGuidance.currentRecipeName || 'Bu tarif'} yerine aynı kategoride daha dengeli adaylar öne alındı.
                                            </p>
                                            <p className="text-xs text-slate-600 dark:text-slate-300">
                                                Liste, bu uyarıyı yükselten metrikte daha düşük yüke sahip tarifleri önce gösterecek şekilde sıralanıyor.
                                            </p>
                                        </div>
                                    ) : null}
                                </div>

                                {/* Category Switcher in Override Mode */}
                                <div className="flex gap-2 overflow-x-auto no-scrollbar py-2">
                                    {categories.filter(c => c.id !== 'ALL').map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => {
                                                setOverrideSlot({ ...overrideSlot, category: cat.id });
                                                setActiveCategory(cat.id);
                                            }}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-[10px] transition-all whitespace-nowrap border ${overrideSlot.category === cat.id
                                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-lg'
                                                : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 text-slate-400 hover:border-primary/30'}`}
                                        >
                                            <span className="material-symbols-outlined text-sm">{cat.icon}</span>
                                            {cat.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <AlakartRecipeGrid
                                recipes={visibleRecipes}
                                draft={planDays}
                                activeDay={overrideSlot.date}
                                activeCategory={overrideSlot.category}
                                handleRecipeClick={handleRecipeClick}
                                handleToggleRecipe={handleToggleRecipe}
                                userProfile={userProfile}
                                portion={portion}
                                recipesLoading={recipesLoading}
                            />
                        </div>
                    ) : (
                        <div className="space-y-8 animate-in fade-in duration-700" id="alakart-otonom-grid">
                            {/* Mobile Weekly Toggle */}
                            <div className="md:hidden flex bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl mb-4 overflow-x-auto no-scrollbar border border-slate-200 dark:border-slate-800">
                                {[0, 1, 2, 3].map(w => (
                                    <button
                                        key={w}
                                        onClick={() => setCurrentWeek(w)}
                                        className={`flex-1 py-3 rounded-xl text-[11px] font-black transition-all whitespace-nowrap px-4 ${currentWeek === w
                                            ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                            : 'text-slate-400'
                                            }`}
                                    >
                                        {w + 1}. HAFTA
                                    </button>
                                ))}
                            </div>

                            <AlakartOtonomGrid
                                weekDates={weekDates}
                                generatedDays={planDays}
                                userOverrides={otonomPlan?.userOverrides || {}}
                                onSlotClick={(date, slot, data) => {
                                    setOverrideGuidance(null);
                                    setActiveDate(date);
                                    setActiveCategory(slot);
                                    setOverrideSlot({ date, category: slot });

                                    // 🟢 FIX: Only set overriding mode if the slot is empty
                                    // If there's an existing recipe, just open the modal for inspection.
                                    // The modal itselt has a "Change" button that triggers isOverriding.
                                    if (!data || !(data.id || data.name)) {
                                        setIsOverriding(true);
                                    }

                                    if (data && data.snapshot) {
                                        setSelectedRecipe({
                                            ...data,
                                            ...data.snapshot,
                                            id: data.id,
                                            name: data.name
                                        });
                                        setRecipeModalOpen(true);
                                    }
                                }}
                                activeDate={activeDate}
                                isExpired={isExpired}
                                categories={categories}
                                onEditDay={handleEditDay}
                                onApproveDay={handleApproveDay}
                                approvedDays={approvedDays}
                                onRemoveSlot={handleRemoveSlot}
                            />

                            {/* Approve All Week Button (Bottom Center) */}
                            {!isExpired && generatedDaysList && generatedDaysList.length > 0 && approvedDays.length < 7 && (
                                <div className="flex justify-center mt-8 pb-8">
                                    <button
                                        onClick={handleApproveAllWeek}
                                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-4 rounded-2xl font-black text-lg shadow-xl shadow-emerald-500/30 hover:scale-105 active:scale-95 transition-all flex items-center gap-3"
                                    >
                                        <span className="material-symbols-outlined text-3xl">done_all</span>
                                        TÜM HAFTAYI ONAYLA
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {recipeModalOpen && selectedRecipe && (
                <RecipeModal
                    onPairingClick={handleRecipeClick}
                    isOpen={recipeModalOpen}
                    onClose={() => setRecipeModalOpen(false)}
                    recipe={selectedRecipe}
                    isDarkMode={isDarkMode}
                    userProfile={userProfile}
                    userPortion={portion}
                    skillLevel={skillLevel}
                    onSelect={handleToggleRecipe}
                    isSelected={otonomPlan ? planDays[activeDate]?.[activeCategory]?.id === selectedRecipe.id : draft[activeDay]?.[activeCategory] === selectedRecipe.id}
                    overrideInfo={currentSlotOverride}
                    showOverrideButton={otonomPlan && !isExpired}
                    onOverrideRequest={() => {
                        setIsOverriding(true);
                        setRecipeModalOpen(false);
                    }}
                    ingredientReservations={ingredientReservations}
                    onMarkAsUsed={async () => {
                        try {
                            const res = await axios.post(`${API_BASE_URL}/api/alakart-menu/consume-day`, {
                                date: activeDate
                            }, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
                            });
                            if (res.data.success) {
                                Swal.fire({ title: 'Afiyet Olsun!', text: 'Malzemeler stoktan düşüldü ve rezervasyonlar güncellendi.', icon: 'success' });
                                fetchOtonomPlan();
                            }
                        } catch (err) {
                            console.error('Consume error:', err);
                            Swal.fire('Hata', 'İşlem sırasında bir hata oluştu.', 'error');
                        }
                    }}
                />
            )}

            {showFinalSummary && (
                <AlakartFinalSummary
                    weeklyPlan={weeklyPlan}
                    consolidatedIngredients={consolidatedIngredients}
                    onClose={() => setShowFinalSummary(false)}
                    isDarkMode={isDarkMode}
                    isApproving={isApproving}
                    onApprove={() => {
                        const allRecipes = [];
                        Object.values(planDays).forEach(day => {
                            Object.values(day).forEach(slot => {
                                if (slot && (slot.id || slot.name)) {
                                    allRecipes.push(slot);
                                }
                            });
                        });
                        approveMenu(allRecipes, {
                            // [A.2] Final özet → tüm hafta onayı: isBatch:true ile commit alışveriş modalı garanti gösterilir.
                            skipInteractiveShopping: true,
                            isBatch: true,
                            suppressBulkShoppingList: true,
                            sourceSurface: 'alakart'
                        });
                    }}
                />
            )}

            {showSurvey && (
                <AlakartPhaseZeroSurvey
                    isDarkMode={isDarkMode}
                    onComplete={async ({ plan: newPlan, config: surveyConfig }) => {
                        setShowSurvey(false);
                        if (surveyConfig) {
                            const nextConfig = {
                                portion: Number(surveyConfig?.portion || 4),
                                meals: Array.isArray(surveyConfig?.categories) ? surveyConfig.categories : []
                            };
                            setAlakartConfig(nextConfig);
                            setPortion(nextConfig.portion);
                            localStorage.setItem('alakartConfig', JSON.stringify(nextConfig));
                        }
                        if (newPlan) {
                            setOtonomPlan(newPlan);
                            setDraft(syncDraftWithPlan(newPlan));
                            setPlanContext(newPlan?.contextSnapshot || null);
                        }
                        await Promise.all([fetchOtonomPlan(), fetchPlanStatus()]);
                    }}
                    onCancel={() => setShowSurvey(false)}
                />
            )}

            {showRatingSummary && otonomPlan && (
                <AlakartRatingSummary
                    planId={otonomPlan.id}
                    onClose={() => setShowRatingSummary(false)}
                    isDarkMode={isDarkMode}
                />
            )}

            {showInviteModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowInviteModal(false)} />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="relative bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] max-w-md w-full shadow-2xl"
                    >
                        <h3 className="text-2xl font-black mb-4">Aile Üyelerini Davet Et 🏠</h3>
                        <p className="text-slate-500 mb-6 font-medium">Bu linki hane halkıyla paylaşarak veto listesi ve tercihlerini 24 saat boyunca girmelerini sağlayabilirsin.</p>
                        <div className="flex gap-2 p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl mb-6">
                            <input readOnly value={inviteLink} className="bg-transparent flex-1 text-xs font-mono outline-none" />
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(inviteLink);
                                    Swal.fire({ title: 'Kopyalandı!', toast: true, position: 'top', timer: 1500, showConfirmButton: false, icon: 'success' });
                                }}
                                className="text-primary font-bold text-xs"
                            >
                                KOPYALA
                            </button>
                        </div>
                        <button
                            onClick={() => setShowInviteModal(false)}
                            className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black"
                        >
                            TAMAM
                        </button>
                    </motion.div>
                </div>
            )}


            {/* 🆕 Rating Invite Modal */}
            {showRatingInviteUI && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowRatingInviteUI(false)} />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="relative bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] max-w-md w-full shadow-2xl border-4 border-emerald-500/20"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <span className="bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400 p-3 rounded-2xl material-symbols-outlined">rate_review</span>
                            <h3 className="text-2xl font-black">Menü Oylama Linki</h3>
                        </div>

                        <p className="text-slate-500 mb-6 font-medium">
                            Bu linki ailenizle paylaşın. Onlar fikirlerini belirtsin, siz de menüyü son haline getirin.
                        </p>

                        <div className="flex gap-2 p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800 rounded-2xl mb-6">
                            <input
                                readOnly
                                value={ratingInviteLink}
                                className="bg-transparent flex-1 text-xs font-mono outline-none text-emerald-800 dark:text-emerald-200"
                            />
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(ratingInviteLink);
                                    Swal.fire({ title: 'Kopyalandı!', toast: true, position: 'top', timer: 1500, showConfirmButton: false, icon: 'success' });
                                }}
                                className="text-emerald-600 font-bold text-xs hover:text-emerald-700"
                            >
                                KOPYALA
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={handleShareViaWhatsApp}
                                className="py-4 bg-[#25D366] text-white rounded-2xl font-black flex items-center justify-center gap-2 hover:brightness-105 active:scale-95 transition-all"
                            >
                                <span className="material-symbols-outlined">chat</span>
                                WHATSAPP
                            </button>
                            <button
                                onClick={() => setShowRatingInviteUI(false)}
                                className="py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black hover:opacity-90 active:scale-95 transition-all"
                            >
                                TAMAM
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            <HealthPlusActivationModal
                isOpen={showHealthPlusModal}
                onClose={() => setShowHealthPlusModal(false)}
                onProceedToPayment={handleHealthPlusProceedToPayment}
            />

            <PremiumPurchaseModal
                isOpen={showHealthPlusPaymentModal}
                onClose={async (reason) => {
                    if (reason === 'open') {
                        setShowHealthPlusPaymentModal(true);
                        return;
                    }
                    setShowHealthPlusPaymentModal(false);
                    if (reason === 'verified') {
                        setHealthPlusEntitled(true);
                        await setHealthPlusActivationState(true);
                        return;
                    }
                    fetchHealthPlusState();
                }}
                preferredModuleKey="global"
                preferredModuleTitle="Sağlık+ Premium"
            />
        </DashboardLayout>
    );
};

export default AlakartMenu;
